import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/hooks/useLocale';
import { LocaleLink } from '@/components/LocaleLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { User, Mail, Lock, Check, AlertTriangle, LogOut, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

export default function AccountSettingsPage() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const isDemo = user?.is_demo;

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileLoading, setProfileLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteEmailConfirm, setDeleteEmailConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [signOutAllLoading, setSignOutAllLoading] = useState(false);

  const isGoogleUser = user?.auth_type === 'google';

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, { name, email }, { withCredentials: true });
      toast.success(t('settings.profileUpdated'));
      // If email changed, the user state needs refreshing — force reload
      if (res.data.email !== user.email) {
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.profileFailed'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.passwordsMismatch'));
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { withCredentials: true });
      toast.success(t('settings.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.passwordFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const confirmDeleteAccount = async () => {
    const reauthMissing = isGoogleUser ? !deleteEmailConfirm : !deletePassword;
    if (reauthMissing) return;
    setDeleteLoading(true);
    try {
      const payload = isGoogleUser
        ? { confirmation: deleteEmailConfirm }
        : { password: deletePassword };
      await axios.delete(`${API}/auth/account`, { data: payload, withCredentials: true });
      toast.success(t('settings.accountDeleted'));
      setDeleteOpen(false);
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.deleteFailed'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSignOutAll = async () => {
    setSignOutAllLoading(true);
    try {
      await axios.post(`${API}/auth/logout-all`, {}, { withCredentials: true });
      toast.success(t('settings.signedOutAll'));
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.signOutAllFailed'));
    } finally {
      setSignOutAllLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="focused-form settings-workspace">
        <h1
          className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-8"
          data-testid="settings-heading"
        >
          {t('settings.title')}
        </h1>

        {isDemo && (
          <Card className="bg-[#B7791F]/[0.06] border-[#B7791F]/25 mb-6" data-testid="demo-banner">
            <CardContent className="py-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#B7791F] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#8A5A0B]">{t('settings.demoBannerTitle')}</p>
                <p className="text-xs text-[#8A5A0B] mt-1">
                  {t('settings.demoBannerDesc')}{' '}
                  <LocaleLink to="/auth" className="underline font-semibold hover:text-[#6E4708]">{t('settings.createFree')}</LocaleLink> {t('settings.unlockFeatures')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Section */}
        <Card className="bg-white border-[#DDE1E5] mb-6">
          <CardHeader>
            <CardTitle className="font-display text-base font-semibold text-[#171A1D] flex items-center gap-2.5">
              <span className="inline-flex w-8 h-8 rounded-lg bg-[#527F19]/10 items-center justify-center shrink-0">
                <User className="w-4 h-4 text-[#527F19]" />
              </span>
              {t('settings.profile')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <Label htmlFor="settings-name" className="text-[#8A929B]">{t('settings.fullName')}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-3 h-4 w-4 text-[#66707A]" />
                  <Input
                    id="settings-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                    required
                    minLength={1}
                    maxLength={100}
                    data-testid="settings-name-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="settings-email" className="text-[#8A929B]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-[#66707A]" />
                  <Input
                    id="settings-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                    required
                    data-testid="settings-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="bg-[#527F19] hover:bg-[#3F6114] text-white"
                disabled={isDemo || profileLoading || (name === user?.name && email === user?.email)}
                data-testid="save-profile-btn"
              >
                {profileLoading ? t('settings.saving') : <><Check className="w-4 h-4 mr-2" /> {t('settings.saveChanges')}</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Section */}
        <Card className="bg-white border-[#DDE1E5] mb-6">
          <CardHeader>
            <CardTitle className="font-display text-base font-semibold text-[#171A1D] flex items-center gap-2.5">
              <span className="inline-flex w-8 h-8 rounded-lg bg-[#527F19]/10 items-center justify-center shrink-0">
                <Lock className="w-4 h-4 text-[#527F19]" />
              </span>
              {t('settings.changePassword')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              if (isDemo) return (
              <p className="text-sm text-[#66707A]" data-testid="demo-password-notice">
                Password management is not available for the demo account. <LocaleLink to="/auth" className="text-[#527F19] hover:underline">{t('settings.createFree')}</LocaleLink> to set your own password.
              </p>
              );
              if (isGoogleUser) return (
              <p className="text-sm text-[#66707A]" data-testid="google-password-notice">
                {t('settings.googlePasswordNotice')}
              </p>
              );
              return (
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current-pass" className="text-[#8A929B]">{t('settings.currentPassword')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#66707A]" />
                    <Input
                      id="current-pass"
                      type="password"
                      value={currentPassword}
                      onChange={e => setCurrentPassword(e.target.value)}
                      className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="new-pass" className="text-[#8A929B]">{t('settings.newPassword')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#66707A]" />
                    <Input
                      id="new-pass"
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                      placeholder={t('settings.newPassPlaceholder')}
                      minLength={8}
                      required
                      data-testid="new-password-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm-new-pass" className="text-[#8A929B]">{t('settings.confirmPassword')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-[#66707A]" />
                    <Input
                      id="confirm-new-pass"
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-white border-[#DDE1E5] text-[#171A1D]"
                      placeholder={t('settings.confirmPassPlaceholder')}
                      minLength={8}
                      required
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="bg-[#527F19] hover:bg-[#3F6114] text-white"
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  data-testid="change-password-btn"
                >
                  {passwordLoading ? t('settings.changingPassword') : t('settings.changePasswordBtn')}
                </Button>
              </form>
              );
            })()}
          </CardContent>
        </Card>

        {/* Security — sign out everywhere */}
        <Card className="bg-white border-[#DDE1E5] mb-6">
          <CardHeader>
            <CardTitle className="font-display text-base font-semibold text-[#171A1D] flex items-center gap-2.5">
              <span className="inline-flex w-8 h-8 rounded-lg bg-[#527F19]/10 items-center justify-center shrink-0">
                <LogOut className="w-4 h-4 text-[#527F19]" />
              </span>
              {t('settings.signOutAll')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#66707A] mb-4">
              {t('settings.signOutAllDesc')}
            </p>
            <Button
              variant="outline"
              className="border-[#DDE1E5] text-[#171A1D] hover:bg-[#F5F6F7] hover:border-[#527F19]/35 transition-colors"
              onClick={handleSignOutAll}
              disabled={isDemo || signOutAllLoading}
              data-testid="sign-out-all-btn"
            >
              {signOutAllLoading ? t('common.loading') : t('settings.signOutAll')}
            </Button>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="bg-white border-red-200">
          <CardHeader>
            <CardTitle className="font-display text-base font-semibold text-red-600 flex items-center gap-2.5">
              <span className="inline-flex w-8 h-8 rounded-lg bg-red-50 items-center justify-center shrink-0">
                <ShieldAlert className="w-4 h-4 text-red-500" />
              </span>
              {t('settings.dangerZone')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#66707A] mb-4">
              {t('settings.deleteDesc')}
            </p>
            <Button
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors"
              onClick={() => { setDeletePassword(''); setDeleteEmailConfirm(''); setDeleteOpen(true); }}
              disabled={isDemo}
              data-testid="delete-account-btn"
            >
              {t('settings.deleteAccount')}
            </Button>
          </CardContent>
        </Card>

        {/* Delete account re-authentication dialog */}
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent className="bg-white text-[#171A1D]" data-testid="delete-account-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> {t('settings.deleteAccount')}
              </DialogTitle>
              <DialogDescription className="text-[#66707A]">
                {isGoogleUser
                  ? t('settings.deleteTypeEmail', { email: user?.email })
                  : t('settings.deleteConfirm')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              {isGoogleUser ? (
                <>
                  <Label htmlFor="delete-email-confirm" className="text-[#8A929B]">Email</Label>
                  <Input
                    id="delete-email-confirm"
                    type="email"
                    value={deleteEmailConfirm}
                    onChange={e => setDeleteEmailConfirm(e.target.value)}
                    placeholder={user?.email}
                    className="mt-1 bg-white border-[#DDE1E5] text-[#171A1D]"
                    data-testid="delete-email-confirm-input"
                  />
                </>
              ) : (
                <>
                  <Label htmlFor="delete-password" className="text-[#8A929B]">{t('settings.currentPassword')}</Label>
                  <Input
                    id="delete-password"
                    type="password"
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    className="mt-1 bg-white border-[#DDE1E5] text-[#171A1D]"
                    data-testid="delete-password-input"
                  />
                </>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteOpen(false)}
                disabled={deleteLoading}
                data-testid="delete-cancel-btn"
              >
                {t('settings.deleteCancelBtn')}
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={confirmDeleteAccount}
                disabled={deleteLoading || (isGoogleUser ? !deleteEmailConfirm : !deletePassword)}
                data-testid="confirm-delete-account-btn"
              >
                {deleteLoading ? t('settings.deleting') : t('settings.deleteAccount')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
